import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';

const YELLOW = '#F4C430';

/**
 * Adjust an inventory item's stock. Two modes:
 * - Quick: a single −1 or +1 click, no reason logged.
 * - Detailed: amount + reason + optional maintenance-event link.
 */
export const AdjustStockModal = ({ open, onClose, onAdjusted, item }) => {
  const [mode, setMode] = useState('quick');
  const [amount, setAmount] = useState(-1);
  const [reason, setReason] = useState('');
  const [printerId, setPrinterId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setMode('quick');
      setAmount(-1);
      setReason('');
      setPrinterId('');
      setError(null);
    }
  }, [open]);

  if (!open || !item) return null;

  const needsPrinterChoice = (item.linkedPrinters?.length ?? 0) >= 2;

  const submit = async (changeAmount) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/inventory/${item.id}/adjust`, {
        changeAmount,
        reason: reason.trim() || null,
        printerId: printerId ? Number(printerId) : null,
      });
      onAdjusted?.(result);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: YELLOW }}>Adjust stock</p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">
            {item.name}
          </p>
          <p class="m-0 mt-0.5 text-[12px] text-text-tertiary">
            Currently <span class="font-serif text-text-primary">{item.stockCount}</span> in stock
          </p>
        </div>

        <div class="space-y-4 p-5">
          {needsPrinterChoice && (
            <div>
              <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
                Which printer <span class="font-medium text-text-muted">· this item is linked to more than one</span>
              </p>
              <select
                value={printerId}
                onChange={(e) => setPrinterId(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">— select —</option>
                {item.linkedPrinters.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {/* Mode toggle */}
          <div class="flex overflow-hidden rounded-md border border-border-strong">
            {[
              { v: 'quick',    label: 'Quick · ±1' },
              { v: 'detailed', label: 'Detailed' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setMode(o.v)}
                class={`flex-1 px-3 py-1.5 text-[13px] font-semibold ${mode === o.v ? 'text-[#332B00]' : 'text-text-tertiary hover:bg-bg-card'}`}
                style={mode === o.v ? { background: YELLOW } : undefined}
              >
                {o.label}
              </button>
            ))}
          </div>

          {mode === 'quick' ? (
            <div class="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => submit(-1)}
                disabled={busy || item.stockCount === 0 || (needsPrinterChoice && !printerId)}
                class="flex-1 text-[15px]"
              >
                − 1 (consume)
              </Button>
              <Button
                variant="secondary"
                onClick={() => submit(+1)}
                disabled={busy || (needsPrinterChoice && !printerId)}
                class="flex-1 text-[15px]"
              >
                + 1 (restock)
              </Button>
            </div>
          ) : (
            <>
              <div>
                <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
                  Change amount <span class="font-medium text-text-muted">· negative consumes, positive restocks</span>
                </p>
                <input
                  type="number"
                  value={amount}
                  onInput={(e) => setAmount(parseInt(e.currentTarget.value, 10) || 0)}
                  class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-center font-serif text-[20px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
                <p class="mt-1 text-[11px] text-text-muted">
                  After change: {Math.max(0, item.stockCount + amount)} in stock
                </p>
              </div>

              <div>
                <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
                  Reason <span class="font-medium text-text-muted">· logged with this change</span>
                </p>
                <textarea
                  rows={2}
                  value={reason}
                  onInput={(e) => setReason(e.currentTarget.value)}
                  placeholder="e.g. Roland weekly maintenance — used 2 swabs"
                  class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>

            </>
          )}

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to adjust')}
            </p>
          )}
        </div>

        {mode === 'detailed' && (
          <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => submit(amount)}
              disabled={busy || amount === 0 || (needsPrinterChoice && !printerId)}
            >
              {busy ? 'Saving…' : `Apply ${amount > 0 ? '+' : ''}${amount}`}
            </Button>
          </div>
        )}
        {mode === 'quick' && (
          <div class="flex justify-end border-t border-border-subtle bg-bg-header px-5 py-3">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          </div>
        )}
      </div>
    </div>
  );
};
